import Server from "../server";
import View from "../framework/view";
import template from "../../pugc/delivery";
import {Order} from "../bo/order";
import {format, parseISO} from "date-fns";
import jQuery from "jquery";
import {MapOrdersNotification, ViewOrderNotification} from "./wrb";
import {clearDeliveries, getDeliveries, setDeliveries, removeDelivery, checkDeliveries} from "../misc/delivery";
import {getDirectionsUrl} from "../misc/orders";
import LocalDB from "../misc/localdb";

import {DEFAULT_POS, LOCALDB_PREFIX, Position} from "./map_orders";
import {autocomplete} from "../google/autocomplete";
import {MapView} from "./map";
import {lastListIds} from "../functions/orders";
import Alerts from "../misc/alerts";
import {User} from "../bo/user";
import {ROLE_CASUAL_DELIVERY} from "../misc/types";

export const calcStateClass = (order: Order) => {
    return order.currentStatus ? "status-" + order.currentStatus.replace(/\s/g, "-") : "status-new";
}

interface Delivery {
    status: "fulfilled",
    value: Order
}

interface Nondelivery {
    status: "rejected",
    reason: string
}

// Global so the selection is "persistent"
const manualSelect: { [id: string]: boolean } = {};

export class DeliveryView extends View {

    static classname = "delivery-view";

    private readonly server = Server.instance();

    private orders : Order[] | undefined = [];

    private user : User | undefined;

    private checkSynchTimer : any;

    constructor() {
        super(DeliveryView.classname);
        this.refresh();
    }

    async refresh() {

        this.user = await this.server.getUser();

        const ids = await getDeliveries();

        lastListIds(ids);

        const orders_ = ids.map((id : string) => this.server.getOrderNoAuth(id));
        const ordersSettled_ = orders_.map((p) => {

            return new Promise<Delivery | Nondelivery>((resolve, reject) => {
                 p.then(value => resolve({
                    "status": "fulfilled",
                    "value": value
                }))
                .catch(reason => resolve({
                    "status": "rejected",
                    "reason": reason
                }));
            });
        });
        const load_ = Promise.all(ordersSettled_);

        load_.then((results) => {

            this.orders = [];
            for (const i in results) {
                const result = results[i];
                if (result.status == "fulfilled") {
                    // @ts-ignore
                    const order = result.value;
                    this.orders.push(order);
                } else {
                    const id = ids[i];
                    console.log("Bad id " + id);
                    removeDelivery(id);
                }
            }
            this.update();
        })
        .catch((err) => {
            console.log("Failed to load deliveries", err);
        });
    }

    getPosition(pos: "origin" | "destination") : Position {
        return LocalDB.instance().get(LOCALDB_PREFIX + pos) || DEFAULT_POS;
    }

    setPosition(pos: "origin" | "destination", data: Position) {
        data.address = data.address.replace(/, Australia$/, "");
        LocalDB.instance().set(LOCALDB_PREFIX + pos, data);
    }

    protected render0(): JQuery<HTMLElement> {

        const renderCreatedDate = (d: string) => {
            return format(parseISO(d), "yyyy-MM-dd HH:mm");
        };

        const renderDeliveryNote = (order: Order) => {
            const notes = [];
            for (const line of order.lineItems || []) {
                // @ts-ignore
                for (const note of line.customTextFields || []) {
                    if (note.title == "Pick up or delivery date:") {
                        notes.push(note.value);
                    }
                }
            }
            const n = notes.join(";")
            return n;
        };

        // Remove leading Add, trailing colon and capitalise first letter
        const prettyOption = (option: any) => {

            const sel = option.selection;
            if (sel.indexOf("Yes") == 0) {
                const str = option.option.replace(/^Add /, "").replace(/:$/, "");
                return str.charAt(0).toUpperCase() + str.slice(1);
            }

            // remove price
            const cleaned = sel.replace(/\$[0-9]+(\.[0-9]+)?\s*/, "");
            return cleaned;
        }

        const origin = this.getPosition("origin");
        const destination = this.getPosition("destination");

        const isCasual = ! this.user || this.user.role === ROLE_CASUAL_DELIVERY;

        const selected = this.orders && this.orders.filter(x => this.isSelected(x));

        const delivered = this.orders && this.orders.filter(x => x.currentStatus == "Delivered");

        const data = {
            orders: this.orders,
            renderCreatedDate: renderCreatedDate,
            renderDeliveryNote: renderDeliveryNote,
            calcStateClass: calcStateClass,
            emptyMessage: isCasual ? "Deliveries will appear here when they are added by the florist." : "Scan an order, then select 'Add to load' to add it to this list.",
            suppressActions: isCasual,
            getDirectionsUrl: getDirectionsUrl,
            prettyOption: prettyOption,
            origin: origin,
            destination: destination,
            isSelected: this.isSelected,
            selected: selected,
            delivered: delivered,
        };

        const out = this.pug(template, data);

        this.afterUpdate(() => {

            this.elt().find("input.address").each((index, element) => {
                const elt = jQuery(element);
                const name = elt.attr("name") as "origin" | "destination";

                autocomplete(element as HTMLInputElement).then((autocomplete) => {
                    autocomplete.addListener("place_changed", () => {
                        const place = autocomplete.getPlace();
                        const data = {
                            address: elt.val() as string,
                            lat: place.geometry?.location?.lat() as number,
                            lng: place.geometry?.location?.lng() as number
                        };

                        this.setPosition(name, data);
                    });
                });
            });
        });

        if (isCasual) {
            if (this.checkSynchTimer) {
                clearTimeout(this.checkSynchTimer);
            }
            const REFRESH_MS = 60 * 1000;

            const check = () => {

                if (this.elt().length == 0) {
                    return;
                }

                checkDeliveries().then(needsUpdate => {
                    if (needsUpdate) {
                        this.server.clearCache();
                        this.refresh();
                    } else {
                        this.checkSynchTimer = setTimeout(check, REFRESH_MS);
                    }
                });

            }

            this.checkSynchTimer = setTimeout(check, REFRESH_MS);
        }

        return out;
    }

    show(id: string) {
        this.notify(new ViewOrderNotification(id));
    }

    async removeAll() {
        const ok = await Alerts.confirmation({
            message: `Really clear deliveries?`,
            options: [ "Yes", "No" ]
        });
        if (ok === "Yes") {
            clearDeliveries();
            this.refresh();
        }
    }

    async removeSelected() {

        if (! this.orders) {
            return;
        }
        const selected = this.orders.filter(x => this.isSelected(x));

        if (selected.length == 0) {
            return;
        }

        const ok = await Alerts.confirmation({
            message: `Really remove ${selected.length} ${selected.length == 1 ? "delivery" : "deliveries"}?`,
            options: [ "Yes", "No" ]
        });
        if (ok === "Yes") {

            for (const order of selected) {
                await removeDelivery(order.id as string)
            }

            this.refresh();
        }
    }

    async removeDelivered() {

        if (! this.orders) {
            return;
        }
        const delivered = this.orders.filter(x => x.currentStatus == "Delivered");

        if (delivered.length == 0) {
            return;
        }

        const ok = await Alerts.confirmation({
            message: `Really remove ${delivered.length} ${delivered.length == 1 ? "delivery" : "deliveries"}?`,
            options: [ "Yes", "No" ]
        });
        if (ok === "Yes") {

            for (const order of delivered) {
                await removeDelivery(order.id as string)
            }

            this.refresh();
        }
    }

    showMap() {
        if (! this.orders) {
            return;
        }
        const selected = this.orders.filter(x => this.isSelected(x));

        const mapOrders = selected.length < this.orders.length ? selected : this.orders;

        const ids = mapOrders.map((order) => order.id).filter((id) => id);
        this.notify(new MapOrdersNotification(ids as string[]));
    }

    isSelected(order: Order) {
        const id = (order as any).id;
        if (manualSelect[id] !== undefined) {
            return manualSelect[id];
        }
    }

    async sort() {
        if (! this.orders) {
            return;
        }

        const origin = this.getPosition("origin");
        const destination = this.getPosition("destination");

        const selected = this.orders.filter(x => this.isSelected(x));
        const unselected = this.orders.filter(x => ! this.isSelected(x));

        const sortOrders = (selected.length > 0 && selected.length < this.orders.length) ? selected : this.orders;
        const otherOrders = (selected.length > 0 &&  selected.length < this.orders.length) ? unselected : [];

        let orders = sortOrders;

        console.log(`Sorting ${sortOrders.length}, other ${otherOrders.length}`);

        try {

            const map = new MapView();

            const data = await map.calcRoute(origin, destination, sortOrders);
            if (data) {
                orders = data.orderedOrders;
            }

        } catch (err) {
            console.log("Failed to calculate optimum order order", err);
        }

        orders.push(...otherOrders);

        if (orders) {
            this.orders = orders;
            const ids = orders.map(x => x.id as string);
            lastListIds(ids);
            setDeliveries(ids);
            this.update();
        }

    }

    setSelected(id: string, selected: boolean) {
        manualSelect[id] = selected;
        this.update([".actions", ".controls"]);
    }

    setSelectAll(select: boolean) {
        if (! this.orders) {
            return;
        }
        for (const order of this.orders) {
            const id = order.id as string;
            manualSelect[id] = select;
        }
        this.update([".orders", ".actions", ".controls"]);
    }

    async useCurrentLocation() {
        navigator.geolocation.getCurrentPosition((position) => {
            this.setPosition("origin", {
                address: `${position.coords.latitude}, ${position.coords.longitude}`,
                lat: position.coords.latitude,
                lng: position.coords.longitude
            });
            this.update();
        });
    }
}

View.on(
    "." + DeliveryView.classname,
    "click",
    ".orderClickable",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        const id = jQuery(elt).closest(".order").data("orderid") as string;
        that.show(id);
    });

View.on(
    "." + DeliveryView.classname,
    "click",
    ".remove-all",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        const id = jQuery(elt).closest(".order").data("orderid") as string;
        that.removeAll();
    });

View.on(
    "." + DeliveryView.classname,
    "click",
    ".remove-selected",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        const id = jQuery(elt).closest(".order").data("orderid") as string;
        that.removeSelected();
    });

View.on(
    "." + DeliveryView.classname,
    "click",
    ".remove-delivered",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        const id = jQuery(elt).closest(".order").data("orderid") as string;
        that.removeDelivered();
    });

View.on(
    "." + DeliveryView.classname,
    "click",
    ".map",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        that.showMap();
    });

View.on(
    "." + DeliveryView.classname,
    "click",
    ".sort",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        that.sort();
    });

View.on(
    "." + DeliveryView.classname,
    "change",
    "input.select[type=checkbox]",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        const id = jQuery(elt).data("id") as string;
        const checked = jQuery(elt).is(":checked");
        that.setSelected(id, checked);
    });

View.on(
    "." + DeliveryView.classname,
    "change",
    "input.select[type=checkbox]",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        const id = jQuery(elt).data("id") as string;
        const checked = jQuery(elt).is(":checked");
        that.setSelected(id, checked);
    });

View.on(
    "." + DeliveryView.classname,
    "change",
    "input.select-all[type=checkbox]",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        const checked = jQuery(elt).is(":checked");
        that.setSelectAll(checked);
    });

View.on(
    "." + DeliveryView.classname,
    "click",
    "a.use-current-location",
    function(that: DeliveryView, event, elt) {
        event.preventDefault();
        that.useCurrentLocation();
    });
//
